import "./Tasks.scss";
import ActionBar from "../../components/ActionBar/ActionBar";
import ActionBarButton from "../../components/ActionBar/ActionBarButton";
import "../../components/ActionBar/ActionBarButton.scss";
import "../../components/ActionBar/ActionBar.scss";
import Spinner from "../../components/Spinner/Spinner";
import EmptyStatePlaceholder from "../../components/EmptyStatePlaceholder/EmptyStatePlaceholder";
import TaskGroup from "./TaskComponents/TaskGroup";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllTasks } from "../../api/tasks/getAll";
import useAuthStore from "../../store/authStore";
import Modal from "../../components/Modal/Modal";
import AddTaskGroup from "./Modals/AddTaskGroup/AddTaskGroup";
import AddBoard from "./Modals/AddBoard/AddBoard";
import ConfigureBoardStates from "./Modals/ConfigureBoardStates/ConfigureBoardStates";
import RemoveCompletedTasks from "./Modals/RemoveCompletedTasks/RemoveCompletedTasks";
import RemoveBoard from "./Modals/RemoveBoard/RemoveBoard";
import { fetcher } from "../../utils/fetcher";
import RightSideBar from "../../components/RightSideBar/RightSideBar";
import RearrangeTaskGroups from "./Modals/RearrangeTaskGroups/RearrangeTaskGroups";
import BoardSidebar from "../../components/BoardSidebar/BoardSidebar";
import ToolTip from "../../components/ToolTip/ToolTip";
import { getAllBoards } from "../../api/boards/getAll";
import { createBoard as createBoardAPI } from "../../api/boards/create";
import { updateBoard } from "../../api/boards/update";
import { updateBoardConfig } from "../../api/boards/updateConfig";
import { deleteBoard as deleteBoardAPI } from "../../api/boards/delete";
import {
  useProjectRealtime,
  useOrganizationRealtime,
  useResync,
} from "../../hooks/useRealtime";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Task from "./TaskComponents/Task";
import FilterSearchBar from "../../components/FilterSearchBar/FilterSearchBar";
import OnboardingPointer from "../../components/OnboardingPointer/OnboardingPointer";
import { useTranslation } from "react-i18next";

function Tasks() {
  const { t } = useTranslation();
  const modalRef = useRef();
  const toolTipRef = useRef();
  const toolTipTimeoutRef = useRef(null);
  const taskGroupsContainerRef = useRef(null);
  const [isSpinnerVisible, setSpinnerVisible] = useState(false);
  const [taskGroups, setTaskGroups] = useState([]);
  const [boards, setBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [addTasksToTop, setAddTasksToTop] = useState(false); // Where new tasks are added in a task group
  const [boardSidebarCollapsed, setBoardSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("boardSidebarCollapsed");
    return saved === "true";
  });
  // False until the project's boards have been fetched (boards is [] meanwhile)
  const [boardsLoaded, setBoardsLoaded] = useState(false);
  const [configModalType, setConfigModalType] = useState(null); // 'status' or 'priority'
  const [showCompleted, setShowCompleted] = useState(false);
  const showCompletedRef = useRef(showCompleted);
  const [activeTask, setActiveTask] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedTaskFromUrl, setSelectedTaskFromUrl] = useState(null);
  const [sortColumn, setSortColumn] = useState(null); // 'title', 'status', 'responsible', 'deadline', 'priority'
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

  const { user, currentProject } = useAuthStore();

  // Keep ref in sync with showCompleted state
  useEffect(() => {
    showCompletedRef.current = showCompleted;
  }, [showCompleted]);

  // Save board sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem("boardSidebarCollapsed", boardSidebarCollapsed);
  }, [boardSidebarCollapsed]);

  // Show the sidebar expanded when the project has no boards, so users see
  // the empty state. Only once boards have loaded (the list is empty while
  // loading), and without changing the user's saved collapsed preference.
  const isBoardSidebarCollapsed =
    boardSidebarCollapsed && !(boardsLoaded && boards.length === 0);

  // Read search query and selectTask from URL on mount and when search params change
  useEffect(() => {
    const searchParam = searchParams.get("search");
    const selectTaskParam = searchParams.get("selectTask");
    const boardIdParam = searchParams.get("boardId");

    if (searchParam) {
      setSearchText(searchParam);
    }

    if (selectTaskParam) {
      setSelectedTaskFromUrl(selectTaskParam);
    }

    if (boardIdParam && boards.length > 0) {
      // Find and switch to the board from URL
      const targetBoard = boards.find((b) => b._id === boardIdParam);
      if (targetBoard && targetBoard._id !== currentBoard?._id) {
        setCurrentBoard(targetBoard);
      }
    }

    // Clear the URL parameters after reading them
    if (searchParam || selectTaskParam || boardIdParam) {
      setTimeout(() => {
        setSearchParams({});
      }, 200);
    }
  }, [searchParams, setSearchParams, boards, currentBoard]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Require 10px movement before drag starts...
      },
    }),
  );

  const showSpinner = () => setSpinnerVisible(true);
  const hideSpinner = () => setSpinnerVisible(false);

  const [isRightSideBarVisible, setRightSideBarVisible] = useState(false);
  const [rightSideBarContent, setRightSideBarContent] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentTab, setCurrentTab] = useState(null);

  const showRightSideBar = (content, taskData = null, tabIndex = null) => {
    // If the same task and same tab is clicked, close the sidebar
    if (
      isRightSideBarVisible &&
      currentTask &&
      taskData &&
      currentTask._id === taskData._id &&
      currentTab === tabIndex
    ) {
      hideRightSideBar();
      return;
    }

    // Otherwise, open or switch the sidebar content
    setRightSideBarContent(content);
    setCurrentTask(taskData);
    setCurrentTab(tabIndex);
    setRightSideBarVisible(true);
  };
  const hideRightSideBar = () => {
    setRightSideBarVisible(false);
    setRightSideBarContent(null);
    setCurrentTask(null);
    setCurrentTab(null);
    // Refresh tasks when sidebar closes to sync any autosaved changes
    fetchTasks();
  };

  // Helper function to strip HTML tags from description
  const stripHtml = (html) => {
    if (!html) return "";
    // Create a temporary div element to parse HTML
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  // Filter tasks based on search text and filters
  const filteredTaskGroups = taskGroups
    .map((group) => {
      let filteredTasks = group.tasks || [];

      // Apply search filter
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();

        // First pass: identify which parent tasks should auto-expand
        const tasksWithFlags = filteredTasks.map((task) => {
          // Skip subtasks in this pass
          if (task.isSubtask || task.parentTaskId) {
            return task;
          }

          // Check if any subtask matches
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => {
              const subtaskTitleMatch = subtask.title
                ?.toLowerCase()
                .includes(searchLower);
              const subtaskDescText = stripHtml(subtask.description);
              const subtaskDescMatch = subtaskDescText
                ?.toLowerCase()
                .includes(searchLower);
              return subtaskTitleMatch || subtaskDescMatch;
            });

          // Return task with shouldAutoExpand flag if subtask matches
          if (subtaskMatches) {
            return { ...task, shouldAutoExpand: true };
          }

          return task;
        });

        // Second pass: filter tasks and include subtasks of matching parents
        const parentTasksToKeep = new Set();

        filteredTasks = tasksWithFlags.filter((task) => {
          // If it's a subtask, check if its parent should be kept
          if (task.isSubtask || task.parentTaskId) {
            return parentTasksToKeep.has(task.parentTaskId);
          }

          // Check if main task matches
          const titleMatch = task.title?.toLowerCase().includes(searchLower);
          const descriptionText = stripHtml(task.description);
          const descriptionMatch = descriptionText
            ?.toLowerCase()
            .includes(searchLower);

          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => {
              const subtaskTitleMatch = subtask.title
                ?.toLowerCase()
                .includes(searchLower);
              const subtaskDescText = stripHtml(subtask.description);
              const subtaskDescMatch = subtaskDescText
                ?.toLowerCase()
                .includes(searchLower);
              return subtaskTitleMatch || subtaskDescMatch;
            });

          const shouldKeep = titleMatch || descriptionMatch || subtaskMatches;
          if (shouldKeep) {
            parentTasksToKeep.add(task._id);
          }
          return shouldKeep;
        });
      }

      // Apply responsible filter
      if (selectedResponsible) {
        // First pass: identify which parent tasks should auto-expand
        const tasksWithFlags = filteredTasks.map((task) => {
          // Skip subtasks in this pass
          if (task.isSubtask || task.parentTaskId) {
            return task;
          }

          // Check if any subtask matches the responsible filter
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => {
              let subtaskResponsibleId;
              if (
                typeof subtask.responsible === "object" &&
                subtask.responsible !== null
              ) {
                subtaskResponsibleId = subtask.responsible._id;
              } else {
                subtaskResponsibleId = null;
              }
              return subtaskResponsibleId === selectedResponsible?.userId;
            });

          // Return task with shouldAutoExpand flag if subtask matches
          if (subtaskMatches) {
            return { ...task, shouldAutoExpand: true };
          }

          return task;
        });

        // Second pass: filter tasks and include subtasks of matching parents
        const parentTasksToKeep = new Set();

        filteredTasks = tasksWithFlags.filter((task) => {
          // If it's a subtask, check if its parent should be kept
          if (task.isSubtask || task.parentTaskId) {
            return parentTasksToKeep.has(task.parentTaskId);
          }

          // Handle different formats of task.responsible
          let taskResponsibleId;
          if (
            typeof task.responsible === "object" &&
            task.responsible !== null
          ) {
            taskResponsibleId = task.responsible._id;
          } else {
            taskResponsibleId = null;
          }

          const match = taskResponsibleId === selectedResponsible?.userId;

          // Check if any subtask matches
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => {
              let subtaskResponsibleId;
              if (
                typeof subtask.responsible === "object" &&
                subtask.responsible !== null
              ) {
                subtaskResponsibleId = subtask.responsible._id;
              } else {
                subtaskResponsibleId = null;
              }
              return subtaskResponsibleId === selectedResponsible?.userId;
            });

          const shouldKeep = match || subtaskMatches;
          if (shouldKeep) {
            parentTasksToKeep.add(task._id);
          }
          return shouldKeep;
        });
      }

      // Apply status filter
      if (selectedStatus) {
        // First pass: identify which parent tasks should auto-expand
        const tasksWithFlags = filteredTasks.map((task) => {
          // Skip subtasks in this pass
          if (task.isSubtask || task.parentTaskId) {
            return task;
          }

          // Check if any subtask matches the status filter
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => subtask.status === selectedStatus);

          // Return task with shouldAutoExpand flag if subtask matches
          if (subtaskMatches) {
            return { ...task, shouldAutoExpand: true };
          }

          return task;
        });

        // Second pass: filter tasks and include subtasks of matching parents
        const parentTasksToKeep = new Set();

        filteredTasks = tasksWithFlags.filter((task) => {
          // If it's a subtask, check if its parent should be kept
          if (task.isSubtask || task.parentTaskId) {
            return parentTasksToKeep.has(task.parentTaskId);
          }

          const match = task.status === selectedStatus;

          // Check if any subtask matches
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => subtask.status === selectedStatus);

          const shouldKeep = match || subtaskMatches;
          if (shouldKeep) {
            parentTasksToKeep.add(task._id);
          }
          return shouldKeep;
        });
      }

      // Apply priority filter
      if (selectedPriority !== null) {
        // First pass: identify which parent tasks should auto-expand
        const tasksWithFlags = filteredTasks.map((task) => {
          // Skip subtasks in this pass
          if (task.isSubtask || task.parentTaskId) {
            return task;
          }

          // Check if any subtask matches the priority filter
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => subtask.priority === selectedPriority);

          // Return task with shouldAutoExpand flag if subtask matches
          if (subtaskMatches) {
            return { ...task, shouldAutoExpand: true };
          }

          return task;
        });

        // Second pass: filter tasks and include subtasks of matching parents
        const parentTasksToKeep = new Set();

        filteredTasks = tasksWithFlags.filter((task) => {
          // If it's a subtask, check if its parent should be kept
          if (task.isSubtask || task.parentTaskId) {
            return parentTasksToKeep.has(task.parentTaskId);
          }

          const match = task.priority === selectedPriority;

          // Check if any subtask matches
          const subtaskMatches = group.tasks
            .filter((t) => t.parentTaskId === task._id && t.isSubtask)
            .some((subtask) => subtask.priority === selectedPriority);

          const shouldKeep = match || subtaskMatches;
          if (shouldKeep) {
            parentTasksToKeep.add(task._id);
          }
          return shouldKeep;
        });
      }

      return {
        ...group,
        tasks: filteredTasks,
      };
    })
    .filter((group) => {
      // Only hide empty groups when there's an active search or filter
      if (
        searchText.trim() ||
        selectedResponsible ||
        selectedStatus ||
        selectedPriority !== null
      ) {
        return group.tasks.length > 0;
      }
      // Show all groups (including empty ones) when no search/filters are active
      return true;
    })
    .map((group) => {
      // Apply sorting if a column is selected
      if (!sortColumn) {
        return group;
      }

      const sortedTasks = [...group.tasks].sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case "title":
            aValue = a.title?.toLowerCase() || "";
            bValue = b.title?.toLowerCase() || "";
            break;
          case "status":
            aValue = a.status || "";
            bValue = b.status || "";
            break;
          case "responsible":
            // Handle responsible being an object or null
            aValue =
              typeof a.responsible === "object" && a.responsible !== null
                ? `${a.responsible.firstName || ""} ${a.responsible.lastName || ""}`.toLowerCase()
                : "";
            bValue =
              typeof b.responsible === "object" && b.responsible !== null
                ? `${b.responsible.firstName || ""} ${b.responsible.lastName || ""}`.toLowerCase()
                : "";
            break;
          case "deadline":
            // Handle deadline - null/undefined should be sorted to the end
            aValue = a.deadline ? new Date(a.deadline).getTime() : Infinity;
            bValue = b.deadline ? new Date(b.deadline).getTime() : Infinity;
            break;
          case "priority":
            // Priority: 1 = High, 2 = Medium, 3 = Low, null/undefined = no priority
            aValue = a.priority ?? 4; // Treat no priority as lower than Low
            bValue = b.priority ?? 4;
            break;
          default:
            return 0;
        }

        // Compare values
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        // Apply sort direction
        return sortDirection === "asc" ? comparison : -comparison;
      });

      // Maintain parent-child relationship for subtasks
      const finalSortedTasks = [];
      sortedTasks.forEach((task) => {
        if (!task.isSubtask && !task.parentTaskId) {
          // Add parent task
          finalSortedTasks.push(task);
          // Add its subtasks right after
          const subtasks = sortedTasks.filter(
            (t) => t.parentTaskId === task._id && t.isSubtask,
          );
          finalSortedTasks.push(...subtasks);
        }
      });

      return {
        ...group,
        tasks: finalSortedTasks,
      };
    });

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const handleResponsibleFilter = (member) => {
    setSelectedResponsible(member);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
  };

  const handlePriorityFilter = (priority) => {
    setSelectedPriority(priority);
  };

  const handleClearFilters = () => {
    setSelectedResponsible(null);
    setSelectedStatus(null);
    setSelectedPriority(null);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Three-state cycle: asc → desc → none
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        // Clear sorting
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const fetchBoards = useCallback(async () => {
    const currentProj = useAuthStore.getState().currentProject;
    if (!currentProj || !currentProj._id) {
      setBoards([]);
      setCurrentBoard(null);
      return;
    }

    try {
      const data = await getAllBoards(currentProj._id);
      setBoards(data);
      setBoardsLoaded(true);

      // Use functional update to avoid depending on currentBoard
      setCurrentBoard((prevBoard) => {
        // Try to restore from localStorage
        const savedBoardId = localStorage.getItem(
          `selectedBoard_${currentProj._id}`,
        );
        if (savedBoardId && !prevBoard) {
          const savedBoard = data.find((b) => b._id === savedBoardId);
          if (savedBoard) {
            return savedBoard;
          }
        }

        // If no board is selected, select the first one
        if (!prevBoard && data.length > 0) {
          return data[0];
        }
        // If a board is selected, update it or switch to first available
        if (prevBoard) {
          const updatedBoard = data.find((b) => b._id === prevBoard._id);
          if (updatedBoard) {
            return updatedBoard;
          } else if (data.length > 0) {
            return data[0];
          }
          return null;
        }
        return null;
      });
    } catch (err) {
      console.error("Error fetching boards:", err);
      setBoards([]);
      setBoardsLoaded(true);
      setCurrentBoard(null);
    }
  }, []); // Remove currentBoard from dependencies

  const fetchTasks = useCallback(
    (options = {}) => {
      const { showLoadingSpinner = false, preserveScroll = false } = options;

      // Save scroll position before fetching if preserveScroll is true
      const scrollPosition =
        preserveScroll && taskGroupsContainerRef.current
          ? taskGroupsContainerRef.current.scrollTop
          : null;

      // Get current project directly from the store to avoid stale closures
      const currentProj = useAuthStore.getState().currentProject;

      // Don't fetch if there's no current project or project ID
      if (!currentProj || !currentProj._id) {
        setTaskGroups([]);
        setIsInitialLoadComplete(true);
        return;
      }

      // Don't fetch tasks if no board is selected
      if (!currentBoard) {
        setTaskGroups([]);
        setIsInitialLoadComplete(true);
        return;
      }

      // Use ref to get the current showCompleted value, avoiding stale closures
      const currentShowCompleted = showCompletedRef.current;
      if (showLoadingSpinner) showSpinner();
      getAllTasks(currentShowCompleted, currentProj._id, currentBoard._id)
        .then((data) => {
          setTaskGroups(data);
          setIsInitialLoadComplete(true);

          // Restore scroll position after React updates
          if (scrollPosition !== null) {
            requestAnimationFrame(() => {
              if (taskGroupsContainerRef.current) {
                taskGroupsContainerRef.current.scrollTop = scrollPosition;
              }
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching sections:", err);
          // If it's a projectId error, clear the sections
          if (err.message?.includes("projectId")) {
            setTaskGroups([]);
          }
          setIsInitialLoadComplete(true);
        })
        .finally(() => {
          if (showLoadingSpinner) hideSpinner();
        });
    },
    [currentBoard],
  );
  const showToolTip = (e, arrow, content) => {
    // Clear any pending close timeout
    if (toolTipTimeoutRef.current) {
      clearTimeout(toolTipTimeoutRef.current);
      toolTipTimeoutRef.current = null;
    }

    const triggerElement = e.currentTarget;

    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeToolTip = () => {
    // Use a small delay to allow moving between buttons
    toolTipTimeoutRef.current = setTimeout(() => {
      if (toolTipRef.current) {
        toolTipRef.current.close();
      }
    }, 50);
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;
    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  // Load the "add new tasks to top/bottom" preference for the selected board
  const currentBoardId = currentBoard?._id;
  useEffect(() => {
    if (!currentBoardId) return;
    const saved = localStorage.getItem(`tasks-addToTop-${currentBoardId}`);
    setAddTasksToTop(saved !== null ? JSON.parse(saved) : false);
  }, [currentBoardId]);

  const handleToggleAddPosition = (toTop) => {
    setAddTasksToTop(toTop);
    if (currentBoard) {
      localStorage.setItem(
        `tasks-addToTop-${currentBoard._id}`,
        JSON.stringify(toTop),
      );
    }
  };

  // Save selected board to localStorage
  useEffect(() => {
    if (currentBoard && currentProject?._id) {
      localStorage.setItem(
        `selectedBoard_${currentProject._id}`,
        currentBoard._id,
      );
    }
  }, [currentBoard, currentProject?._id]);

  useEffect(() => {
    // Fetch boards when project changes
    if (currentProject?._id) {
      fetchBoards();
    } else {
      setBoards([]);
      setCurrentBoard(null);
    }
  }, [currentProject?._id, fetchBoards]);

  useEffect(() => {
    // Fetch tasks whenever currentProject, currentBoard, or showCompleted changes
    if (currentProject?._id && currentBoard) {
      fetchTasks({ showLoadingSpinner: true });
    }
  }, [currentProject?._id, currentBoard, showCompleted, fetchTasks]);

  // Listen for project member updates to refresh tasks
  useEffect(() => {
    const handleMembersUpdated = () => {
      fetchTasks();
    };

    const handleUserProfileUpdated = () => {
      fetchTasks();
    };

    window.addEventListener("projectMembersUpdated", handleMembersUpdated);
    window.addEventListener("userProfileUpdated", handleUserProfileUpdated);

    return () => {
      window.removeEventListener("projectMembersUpdated", handleMembersUpdated);
      window.removeEventListener(
        "userProfileUpdated",
        handleUserProfileUpdated,
      );
    };
  }, [fetchTasks]);

  // Reload when this tab may have missed changes made on another device
  useResync(() => {
    fetchBoards();
    fetchTasks({ preserveScroll: true });
  });

  // Real-time WebSocket updates
  useProjectRealtime(currentProject?._id, {
    onTaskCreated: () => {
      fetchTasks();
    },
    onTaskUpdated: () => {
      fetchTasks({ preserveScroll: true });
    },
    onTaskDeleted: () => {
      fetchTasks();
    },
    onTaskReordered: () => {
      fetchTasks();
    },
    onTaskBulkDeleted: () => {
      fetchTasks();
    },
    onSectionCreated: () => {
      fetchTasks();
    },
    onSectionUpdated: () => {
      fetchTasks();
    },
    onSectionDeleted: () => {
      fetchTasks();
    },
    onBoardCreated: () => {
      fetchBoards();
    },
    onBoardUpdated: () => {
      fetchBoards();
    },
    onBoardDeleted: () => {
      fetchBoards();
    },
    onBoardsReordered: () => {
      fetchBoards();
    },
  });

  // Listen for organization-level user updates (e.g., profile color changes)
  useOrganizationRealtime(user?.currentOrganization, {
    onUserUpdated: () => {
      fetchTasks();
    },
  });

  // Update currentTask when taskGroups changes (e.g., after fetching or real-time updates)
  useEffect(() => {
    if (currentTask && taskGroups.length > 0) {
      // Find the updated task in the taskGroups
      let updatedTask = null;
      for (const group of taskGroups) {
        updatedTask = group.tasks.find((task) => task._id === currentTask._id);
        if (updatedTask) break;
      }

      // If we found the updated task, update currentTask state
      if (updatedTask) {
        setCurrentTask(updatedTask);
      }
    }
  }, [taskGroups, currentTask?._id]);

  const createTaskGroup = async (name, color) => {
    if (!currentBoard) {
      console.error("No board selected");
      return;
    }

    try {
      // showSpinner();
      await fetcher.post("/api/sections", {
        projectId: currentProject._id,
        boardId: currentBoard._id,
        sortOrder: taskGroups.length,
        name,
        color,
        isActive: true,
      });
      fetchTasks();
      if (modalRef.current) modalRef.current.close();
    } catch (err) {
      console.error("Error creating section:", err);
    } finally {
      // hideSpinner();
    }
  };

  const createBoardHandler = async (name, icon) => {
    try {
      const newBoard = await createBoardAPI({
        projectId: currentProject._id,
        name,
        icon,
        sortOrder: boards.length,
      });
      await fetchBoards();
      setCurrentBoard(newBoard);
      if (modalRef.current) modalRef.current.close();
    } catch (err) {
      console.error("Error creating board:", err);
    }
  };

  const editBoardHandler = async (board) => {
    openModal(
      { currentTarget: document.body },
      "center-center",
      <AddBoard
        createBoard={async (name, icon) => {
          try {
            await updateBoard(board._id, { name, icon });
            await fetchBoards();
            if (modalRef.current) modalRef.current.close();
          } catch (err) {
            console.error("Error updating board:", err);
          }
        }}
        cancelCreateBoard={cancelCreateBoard}
        existingBoard={board}
      />,
    );
  };

  const deleteBoardHandler = (board) => {
    openModal(
      { currentTarget: null },
      "center-center",
      <RemoveBoard
        boardName={board.name}
        removeBoard={async () => {
          modalRef.current?.close();
          try {
            await deleteBoardAPI(board._id);
            await fetchBoards();
          } catch (err) {
            console.error("Error deleting board:", err);
          }
        }}
        cancelRemoveBoard={() => modalRef.current?.close()}
      />,
    );
  };

  const openConfigureStatus = () => {
    if (!currentBoard) return;
    setConfigModalType("status");
    openModal(
      { currentTarget: null },
      "center-center",
      <ConfigureBoardStates
        type="status"
        initialConfig={currentBoard.statusConfig || []}
        onSave={saveStatusConfig}
        onCancel={() => modalRef.current?.close()}
      />,
    );
  };

  const openConfigurePriority = () => {
    if (!currentBoard) return;
    setConfigModalType("priority");
    openModal(
      { currentTarget: null },
      "center-center",
      <ConfigureBoardStates
        type="priority"
        initialConfig={currentBoard.priorityConfig || []}
        onSave={savePriorityConfig}
        onCancel={() => modalRef.current?.close()}
      />,
    );
  };

  const saveStatusConfig = async (newConfig) => {
    try {
      await updateBoardConfig(currentBoard._id, { statusConfig: newConfig });
      await fetchBoards();
      if (modalRef.current) modalRef.current.close();
      setConfigModalType(null);
    } catch (err) {
      console.error("Error updating status config:", err);
    }
  };

  const savePriorityConfig = async (newConfig) => {
    try {
      await updateBoardConfig(currentBoard._id, {
        priorityConfig: newConfig,
      });
      await fetchBoards();
      if (modalRef.current) modalRef.current.close();
      setConfigModalType(null);
    } catch (err) {
      console.error("Error updating priority config:", err);
    }
  };

  const cancelCreateBoard = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const cancelCreateTaskGroup = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const closeRearrangeTaskGroups = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const removeCompletedTasks = async () => {
    try {
      // showSpinner();
      await fetcher.delete(
        `/api/tasks/completed?projectId=${currentProject._id}`,
      );
      fetchTasks();
      setShowCompleted(false); // Hide completed items after deletion
      if (modalRef.current) modalRef.current.close();
    } catch (err) {
      console.error("Error deleting completed tasks:", err);
    } finally {
      // hideSpinner();
    }
  };

  const cancelRemoveCompletedTasks = () => {
    if (modalRef.current) modalRef.current.close();
  };

  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const collapseAll = () => {
    const newState = {};
    taskGroups.forEach((g) => {
      newState[g?.name] = true;
    });
    setCollapsedGroups(newState);
  };

  const expandAll = () => {
    const newState = {};
    taskGroups.forEach((g) => {
      newState[g?.name] = false;
    });
    setCollapsedGroups(newState);
  };

  const handleDragStart = (event) => {
    const { active } = event;

    // Find the task being dragged
    for (const group of taskGroups) {
      const task = group.tasks.find((t) => t._id === active.id);
      if (task) {
        setActiveTask(task);
        break;
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Check if dropping on an empty task group
    const isEmptyDropZone = over.id.toString().startsWith("empty-");

    // Find source task and group
    let sourceGroup = null;
    let sourceTaskIndex = -1;

    for (const group of taskGroups) {
      const taskIndex = group.tasks.findIndex((t) => t._id === active.id);
      if (taskIndex !== -1) {
        sourceGroup = group;
        sourceTaskIndex = taskIndex;
        break;
      }
    }

    if (!sourceGroup) {
      return;
    }

    // Handle dropping on empty task group
    if (isEmptyDropZone) {
      const targetGroupId = over.id.toString().replace("empty-", "");
      const targetGroup = taskGroups.find((g) => g._id === targetGroupId);

      if (!targetGroup || sourceGroup._id === targetGroup._id) {
        return;
      }

      const newTaskGroups = taskGroups.map((g) => ({
        ...g,
        tasks: [...g.tasks],
      }));

      const sourceGroupIndex = newTaskGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newTaskGroups.findIndex(
        (g) => g._id === targetGroupId,
      );

      // Remove from source
      const [movedTask] = newTaskGroups[sourceGroupIndex].tasks.splice(
        sourceTaskIndex,
        1,
      );

      // If moving a parent task, also move its subtasks
      let movedSubtasks = [];
      if (!movedTask.isSubtask && !movedTask.parentTaskId) {
        // Find and remove all subtasks of this task
        movedSubtasks = newTaskGroups[sourceGroupIndex].tasks.filter(
          (t) => t.parentTaskId === movedTask._id && t.isSubtask,
        );
        newTaskGroups[sourceGroupIndex].tasks = newTaskGroups[
          sourceGroupIndex
        ].tasks.filter(
          (t) => !(t.parentTaskId === movedTask._id && t.isSubtask),
        );
      }

      // Add to empty target group
      newTaskGroups[targetGroupIndex].tasks.push(movedTask);

      // Add subtasks after the parent
      if (movedSubtasks.length > 0) {
        newTaskGroups[targetGroupIndex].tasks.push(...movedSubtasks);
      }

      // Update UI immediately
      setTaskGroups(newTaskGroups);
      return;
    }

    // Find target task and group (existing logic)
    let targetGroup = null;
    let targetTaskIndex = -1;

    for (const group of taskGroups) {
      const taskIndex = group.tasks.findIndex((t) => t._id === over.id);
      if (taskIndex !== -1) {
        targetGroup = group;
        targetTaskIndex = taskIndex;
        break;
      }
    }

    if (!sourceGroup || !targetGroup) {
      return;
    }

    // Only update if moving between groups or different positions
    if (
      sourceGroup._id !== targetGroup._id ||
      sourceTaskIndex !== targetTaskIndex
    ) {
      const newTaskGroups = taskGroups.map((g) => ({
        ...g,
        tasks: [...g.tasks],
      }));

      const sourceGroupIndex = newTaskGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newTaskGroups.findIndex(
        (g) => g._id === targetGroup._id,
      );

      // Remove from source
      const [movedTask] = newTaskGroups[sourceGroupIndex].tasks.splice(
        sourceTaskIndex,
        1,
      );

      // If moving a parent task, also move its subtasks
      let movedSubtasks = [];
      if (!movedTask.isSubtask && !movedTask.parentTaskId) {
        // Find and remove all subtasks of this task
        movedSubtasks = newTaskGroups[sourceGroupIndex].tasks.filter(
          (t) => t.parentTaskId === movedTask._id && t.isSubtask,
        );
        newTaskGroups[sourceGroupIndex].tasks = newTaskGroups[
          sourceGroupIndex
        ].tasks.filter(
          (t) => !(t.parentTaskId === movedTask._id && t.isSubtask),
        );
      }

      // Insert into target position
      newTaskGroups[targetGroupIndex].tasks.splice(
        targetTaskIndex,
        0,
        movedTask,
      );

      // Insert subtasks right after the parent task
      if (movedSubtasks.length > 0) {
        newTaskGroups[targetGroupIndex].tasks.splice(
          targetTaskIndex + 1,
          0,
          ...movedSubtasks,
        );
      }

      // Update UI immediately
      setTaskGroups(newTaskGroups);
    }
  };

  const handleDragEnd = async (event) => {
    const { over } = event;

    // Clear the active task
    setActiveTask(null);

    if (!over) {
      return;
    }

    // Note: We can't check if active.id === over.id because handleDragOver
    // has already updated the state, so we need to persist the changes

    // The UI has already been updated by handleDragOver, so we need to use the CURRENT state
    // to generate the correct sort orders for the backend
    const updates = [];

    // Assign sortOrder to ALL tasks (main + subtasks) sequentially to avoid duplicates
    taskGroups.forEach((group) => {
      let currentSortOrder = 0;

      // Get main tasks first
      const mainTasks = group.tasks.filter(
        (task) => !task.isSubtask && !task.parentTaskId,
      );

      mainTasks.forEach((task) => {
        // Assign sortOrder to the main task
        updates.push({
          _id: task._id,
          sortOrder: currentSortOrder++,
          sectionId: group._id,
        });

        // Assign sortOrder to subtasks right after parent
        const subtasks = group.tasks.filter(
          (t) => t.parentTaskId === task._id && t.isSubtask,
        );
        subtasks.forEach((subtask) => {
          updates.push({
            _id: subtask._id,
            sectionId: group._id,
            sortOrder: currentSortOrder++, // Sequential sortOrder for all tasks
          });
        });
      });
    });

    if (updates.length === 0) {
      return;
    }

    try {
      await fetcher.put("/api/tasks/reorder", updates);
      await fetchTasks();
    } catch (error) {
      console.error("Error during reorder:", error);
      // Revert on error
      await fetchTasks();
    }
  };

  // Check if user should see onboarding pointer for task groups
  const isNotFiltering =
    !searchText.trim() &&
    !selectedResponsible &&
    !selectedStatus &&
    selectedPriority === null;
  const hasNoBoards =
    currentProject &&
    boards.length === 0 &&
    isNotFiltering &&
    isInitialLoadComplete;
  const hasNoSections =
    currentProject &&
    taskGroups.length === 0 &&
    isNotFiltering &&
    isInitialLoadComplete &&
    boards.length > 0; // Only show section onboarding if boards exist
  const canCreateSection =
    currentProject &&
    (currentProject.myProjectRole === "admin" ||
      currentProject.myProjectRole === "user");
  const showBoardOnboarding = hasNoBoards && canCreateSection;
  const showSectionOnboarding = hasNoSections && canCreateSection;

  return (
    <div className="tasks-wrapper">
      <Modal ref={modalRef}></Modal>
      <ToolTip ref={toolTipRef} manualControl={true} />
      <OnboardingPointer
        targetSelector=".add-board-button"
        message={t("tasks.board.add")}
        show={showBoardOnboarding}
      />
      <OnboardingPointer
        targetSelector=".add-section-button"
        message={t("tasks.group.createOnboarding")}
        show={showSectionOnboarding}
      />
      <ActionBar>
        {currentProject &&
          (currentProject.myProjectRole === "admin" ||
            currentProject.myProjectRole === "user") && (
            <>
              <ActionBarButton
                className="add-board-button"
                buttonText={t("tasks.board.add")}
                icon={
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "20px" }}
                  >
                    dashboard
                  </span>
                }
                onClick={(e) =>
                  openModal(
                    e || { currentTarget: document.body },
                    "center-center",
                    <AddBoard
                      createBoard={createBoardHandler}
                      cancelCreateBoard={cancelCreateBoard}
                    />,
                  )
                }
              />
              {currentBoard && (
                <>
                  <div className="action-bar-spacer" />
                  <ActionBarButton
                    className="add-section-button"
                    buttonText={t("tasks.group.add")}
                    icon={
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "20px" }}
                      >
                        assignment_add
                      </span>
                    }
                    onClick={(e) =>
                      openModal(
                        e,
                        "auto-top-left",
                        <AddTaskGroup
                          createTaskGroup={createTaskGroup}
                          cancelCreateTaskGroup={cancelCreateTaskGroup}
                        />,
                      )
                    }
                  />
                </>
              )}
              <div className="action-bar-spacer" />
            </>
          )}
        <ActionBarButton
          buttonText={t("tasks.actions.collapseAll")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              collapse_content
            </span>
          }
          onClick={collapseAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText={t("tasks.actions.expandAll")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              expand_content
            </span>
          }
          onClick={expandAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText={t("tasks.actions.rearrangeGroups")}
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              move_down
            </span>
          }
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <RearrangeTaskGroups
                taskGroups={taskGroups}
                closeRearrangeTaskGroups={closeRearrangeTaskGroups}
                onSave={fetchTasks}
              />,
            )
          }
        />
        <div className="action-bar-spacer" />
        {showCompleted ? (
          <ActionBarButton
            buttonText={t("tasks.actions.hideCompleted")}
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility_off
              </span>
            }
            onClick={() => setShowCompleted(false)}
          />
        ) : (
          <ActionBarButton
            buttonText={t("tasks.actions.showCompleted")}
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility
              </span>
            }
            onClick={() => setShowCompleted(true)}
          />
        )}
        {showCompleted ? (
          <>
            <div className="action-bar-spacer" />
            <ActionBarButton
              buttonText={t("tasks.actions.removeCompleted")}
              textColor="#e8697d"
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px", color: "#e8697d" }}
                >
                  delete
                </span>
              }
              onClick={(e) =>
                openModal(
                  e,
                  "auto-top-center",
                  <RemoveCompletedTasks
                    cancelRemoveCompletedTasks={cancelRemoveCompletedTasks}
                    removeCompletedTasks={removeCompletedTasks}
                  />,
                )
              }
            />
          </>
        ) : (
          <></>
        )}
      </ActionBar>
      <div className="tasks">
        <div className="tasks-content">
          {currentProject?._id && (
            <div
              className={`tasks-left-column ${isBoardSidebarCollapsed ? "collapsed" : ""}`}
            >
              <div className="tasks-column-header">
                {!isBoardSidebarCollapsed && (
                  <span>{t("tasks.board.boards")}</span>
                )}
                <div className="tasks-column-header-actions">
                  {!isBoardSidebarCollapsed && canCreateSection && (
                    <span
                      className="material-symbols-outlined"
                      onClick={(e) =>
                        openModal(
                          e || { currentTarget: document.body },
                          "center-center",
                          <AddBoard
                            createBoard={createBoardHandler}
                            cancelCreateBoard={cancelCreateBoard}
                          />,
                        )
                      }
                      style={{
                        cursor: "pointer",
                        fontSize: "18px",
                      }}
                      title={t("tasks.board.add")}
                    >
                      add
                    </span>
                  )}
                  <button
                    className="collapse-toggle"
                    onClick={() =>
                      setBoardSidebarCollapsed(!isBoardSidebarCollapsed)
                    }
                    onMouseEnter={(e) =>
                      showToolTip(
                        e,
                        "left-center",
                        isBoardSidebarCollapsed
                          ? t("tasks.board.expandSidebar")
                          : t("tasks.board.collapseSidebar"),
                      )
                    }
                    onMouseLeave={closeToolTip}
                  >
                    <span className="material-symbols-outlined">
                      {isBoardSidebarCollapsed
                        ? "keyboard_double_arrow_right"
                        : "keyboard_double_arrow_left"}
                    </span>
                  </button>
                </div>
              </div>
              <BoardSidebar
                boards={boards}
                currentBoard={currentBoard}
                onBoardSelect={setCurrentBoard}
                onCreateBoard={(e) =>
                  openModal(
                    e || { currentTarget: document.body },
                    "center-center",
                    <AddBoard
                      createBoard={createBoardHandler}
                      cancelCreateBoard={cancelCreateBoard}
                    />,
                  )
                }
                onEditBoard={editBoardHandler}
                onDeleteBoard={deleteBoardHandler}
                canManageBoards={canCreateSection}
                isCollapsed={isBoardSidebarCollapsed}
                showToolTip={showToolTip}
                closeToolTip={closeToolTip}
              />
            </div>
          )}
          <div className="tasks-right-column">
            <div className="tasks-column-header">
              <span>{currentBoard?.name || t("tasks.title")}</span>
              {currentProject &&
                (currentProject.myProjectRole === "admin" ||
                  currentProject.myProjectRole === "user") && (
                  <span
                    className="material-symbols-outlined"
                    onClick={(e) =>
                      openModal(
                        e,
                        "auto-top-right",
                        <AddTaskGroup
                          createTaskGroup={createTaskGroup}
                          cancelCreateTaskGroup={cancelCreateTaskGroup}
                        />,
                      )
                    }
                    style={{
                      marginLeft: "auto",
                      cursor: "pointer",
                      fontSize: "18px",
                    }}
                    title={t("tasks.group.add")}
                  >
                    add
                  </span>
                )}
            </div>
            {isSpinnerVisible && <Spinner />}
            <RightSideBar
              isVisible={isRightSideBarVisible}
              content={rightSideBarContent}
              currentTask={currentTask}
              fetchTasks={fetchTasks}
              hideRightSideBar={hideRightSideBar}
            />
            {currentProject?._id && (
              <div className="tasks-header-bar">
                <FilterSearchBar
                  searchValue={searchText}
                  onSearchChange={handleSearchChange}
                  onResponsibleFilter={handleResponsibleFilter}
                  onStatusFilter={handleStatusFilter}
                  onPriorityFilter={handlePriorityFilter}
                  onClearFilters={handleClearFilters}
                  selectedResponsible={selectedResponsible}
                  selectedStatus={selectedStatus}
                  selectedPriority={selectedPriority}
                  statusConfig={currentBoard?.statusConfig || []}
                  priorityConfig={currentBoard?.priorityConfig || []}
                />
                <button
                  className={`tasks-add-position-btn ${addTasksToTop ? "active" : ""}`}
                  onClick={() => handleToggleAddPosition(true)}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("tasks.actions.addToTop"))
                  }
                  onMouseLeave={closeToolTip}
                >
                  <span className="material-symbols-outlined">
                    vertical_align_top
                  </span>
                </button>
                <button
                  className={`tasks-add-position-btn ${!addTasksToTop ? "active" : ""}`}
                  onClick={() => handleToggleAddPosition(false)}
                  onMouseEnter={(e) =>
                    showToolTip(e, "top-center", t("tasks.actions.addToBottom"))
                  }
                  onMouseLeave={closeToolTip}
                >
                  <span className="material-symbols-outlined">
                    vertical_align_bottom
                  </span>
                </button>
              </div>
            )}
            {!currentProject?._id ? (
              <EmptyStatePlaceholder
                topText={t("tasks.empty.noProject")}
                bottomText={t("tasks.empty.noProjectHint")}
              />
            ) : (
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredTaskGroups.flatMap((group) =>
                    group.tasks.map((task) => task._id),
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredTaskGroups.length === 0 ? (
                    <EmptyStatePlaceholder
                      topText={t("tasks.empty.noTasks")}
                      bottomText={
                        searchText.trim() ||
                        selectedResponsible ||
                        selectedStatus ||
                        selectedPriority !== null
                          ? t("tasks.empty.adjustFilters")
                          : boards.length === 0
                            ? t("tasks.empty.createBoard")
                            : t("tasks.empty.createSection")
                      }
                    />
                  ) : (
                    <div
                      className="taskgroups-container"
                      ref={taskGroupsContainerRef}
                    >
                      {filteredTaskGroups.map((taskGroup) => (
                        <TaskGroup
                          key={taskGroup._id}
                          name={taskGroup?.name}
                          data={taskGroup}
                          fetchTasks={fetchTasks}
                          collapsed={!!collapsedGroups[taskGroup?.name]}
                          onToggle={() => toggleGroup(taskGroup?.name)}
                          showRightSideBar={showRightSideBar}
                          hideRightSideBar={hideRightSideBar}
                          taskGroups={taskGroups}
                          showCompleted={showCompleted}
                          selectedTaskId={
                            isRightSideBarVisible
                              ? currentTask?._id
                              : selectedTaskFromUrl
                          }
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          onConfigureStatus={openConfigureStatus}
                          onConfigurePriority={openConfigurePriority}
                          statusConfig={currentBoard?.statusConfig || []}
                          priorityConfig={currentBoard?.priorityConfig || []}
                          addToTop={addTasksToTop}
                        />
                      ))}
                    </div>
                  )}
                </SortableContext>
                <DragOverlay>
                  {activeTask ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "40px 40px 2fr 120px 60px 40px 40px 60px 120px 3fr 40px 40px 40px",
                        alignItems: "center",
                        justifyItems: "stretch",
                        backgroundColor: "var(--task-background-color)",
                        border: "1px solid var(--task-line-color)",
                        borderRadius: "5px",
                        opacity: 0.9,
                        cursor: "grabbing",
                      }}
                    >
                      <Task
                        data={activeTask}
                        fetchTasks={fetchTasks}
                        showRightSideBar={() => {}}
                        hideRightSideBar={() => {}}
                      />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tasks;
